import { useState } from "react";
import { Bot, ChevronDown } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useFetcher } from "~/hooks/useFetcher";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { AgentPickList } from "~/pages/manifold-nextgen/components/agents/AgentPickList";
import { AgentStatus, type Agent } from "~/services/agents";
import { projectAgentsPath } from "~/services/projects";

interface AgentSwitcherPopoverProps {
  projectId: string;
  // The agent selected for the next turn — highlighted in the list and named on
  // the trigger. Owned by the parent so the same value labels the composer and
  // rides the next send. Null before an agent has been resolved.
  currentAgentId?: string | null;
  // Notifies the parent of the pick so it can advance the active next-turn agent;
  // the trigger reflects the switch when that flows back in through
  // `currentAgentId`. The selection rides the next send — nothing is written
  // server-side here.
  onSwitched?: (agentId: string) => void;
  // The selection takes effect on the next send, so the control is inert while a
  // run streams (there is no next send to attach it to yet).
  disabled?: boolean;
}

// Composer-row switcher for the conversation's next-turn agent. Lists the
// project's usable agents and updates the composer's active-agent selection on
// pick; the choice rides the next send, so it takes effect on the following
// turn. Rendered only for project sessions, and hides itself when fewer than
// two usable (ENABLED) agents are on the roster — there is nothing to switch
// between.
export const AgentSwitcherPopover = ({
  projectId,
  currentAgentId,
  onSwitched,
  disabled = false,
}: AgentSwitcherPopoverProps) => {
  const { showSuccess } = useToast();
  const [open, setOpen] = useState(false);

  const { data: roster } = useFetcher<Agent[]>(projectAgentsPath(projectId));

  // Only ENABLED agents may be picked to handle a turn. A DISABLED agent stays
  // attached (its historic turns still surface) but is never selectable here —
  // the same ENABLED-only rule NewSessionPicker applies when starting a session.
  const usableAgents = (roster ?? []).filter((agent) => agent.status === AgentStatus.ENABLED);

  // Nothing to switch between until at least two usable agents are on the roster.
  if (usableAgents.length < 2) {
    return null;
  }

  const activeAgentId = currentAgentId ?? null;
  const activeAgent = usableAgents.find((agent) => agent.id === activeAgentId);

  const handlePick = (agentId: string) => {
    setOpen(false);
    const picked = usableAgents.find((agent) => agent.id === agentId);
    if (!picked || agentId === activeAgentId) return;
    // Local selection only — the parent advances the active agent and the next
    // send forwards it. Nothing is persisted server-side on pick.
    onSwitched?.(agentId);
    showSuccess(`${picked.display_name} will answer your next message.`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              aria-label={`Switch agent — currently ${activeAgent?.display_name ?? "unset"}`}
              className="h-8 gap-1 px-2 text-muted-foreground"
            >
              {activeAgent ? (
                <AgentAvatar agent={activeAgent} size={18} aria-hidden />
              ) : (
                <Bot className="size-3.5" />
              )}
              {/* Fixed width (not max-width) so the trigger — and the whole
                  toolbar row — keeps a constant width across agents whose names
                  differ in length; longer names ellipsize via `truncate`. */}
              <span className="w-32 truncate text-left text-[13px]">
                {activeAgent?.display_name ?? "Switch agent"}
              </span>
              <ChevronDown className="size-3.5 text-muted-foreground/70" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Switch agent</TooltipContent>
      </Tooltip>
      <PopoverContent
        align="start"
        className="w-96 p-0"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b px-3 py-2">
          <p className="text-sm font-semibold">Switch agent</p>
          <p className="text-xs text-muted-foreground">
            Takes effect on your next message. Earlier replies stay with the agent that wrote them.
          </p>
        </div>
        <div className="p-1">
          <AgentPickList agents={usableAgents} selectedId={activeAgentId} onSelect={handlePick} />
        </div>
      </PopoverContent>
    </Popover>
  );
};
